import { AxiosError } from 'axios';
import { apiService } from '@/shared/services/apiServices';
import { ASSIGN_TECHNICIAN_ERROR_MESSAGES } from '../errors/errorMessages';
import type {
  AssignTechnicianPayload,
  AssignTechnicianResponseData,
  AssignTechnicianResult,
  AssigneeType,
} from '../types/assignTechnicianType';
import serviceRequestEndpoints from './serviceRequestEndpoints';

/** A 409 whose body signals the assignee is already booked for the window. */
const isAssignmentConflict = (errorData: unknown, status?: number): boolean => {
  if (status !== 409 || !errorData || typeof errorData !== 'object') {
    return false;
  }
  const obj = errorData as { error?: string };
  return (
    typeof obj.error === 'string' && /unavailable|conflicting/i.test(obj.error)
  );
};

const extractErrorMessage = (
  errorData: unknown,
  status?: number,
  assigneeType: AssigneeType = 'technician'
): string => {
  if (status === 401 || status === 403) {
    return ASSIGN_TECHNICIAN_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return ASSIGN_TECHNICIAN_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (errorData && typeof errorData === 'object') {
    const obj = errorData as { message?: string; error?: string };
    if (typeof obj.error === 'string' && obj.error.trim().length > 0) {
      // Map specific 409 strings to localized copy.
      if (status === 409 && /status changed/i.test(obj.error)) {
        return ASSIGN_TECHNICIAN_ERROR_MESSAGES.STATUS_CHANGED;
      }
      if (status === 409 && /unavailable|conflicting/i.test(obj.error)) {
        return assigneeType === 'admin'
          ? ASSIGN_TECHNICIAN_ERROR_MESSAGES.ADMIN_CONFLICT
          : ASSIGN_TECHNICIAN_ERROR_MESSAGES.TECHNICIAN_CONFLICT;
      }
      return obj.error;
    }
    if (typeof obj.message === 'string' && obj.message.trim().length > 0) {
      return obj.message;
    }
  }
  return ASSIGN_TECHNICIAN_ERROR_MESSAGES.DEFAULT_ERROR;
};

export const assignTechnician = async ({
  serviceRequestId,
  technicianId,
  assigneeType = 'technician',
  startDate,
  endDate,
  startTime,
  endTime,
  adminNotes,
  formId,
}: AssignTechnicianPayload): Promise<AssignTechnicianResult> => {
  const trimmedId = serviceRequestId?.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  const trimmedNotes = typeof adminNotes === 'string' ? adminNotes.trim() : '';

  // Admin: send the canonical discriminated body (assignee_type + assignee_id).
  // Technician: keep the legacy `technician_id` body (which the BE treats as
  // assignee_type=TECHNICIAN), so the existing flow is byte-identical.
  const body: Record<string, unknown> =
    assigneeType === 'admin'
      ? {
          assignee_type: 'ADMIN',
          assignee_id: technicianId,
          start_date: startDate,
          end_date: endDate,
        }
      : {
          technician_id: technicianId,
          start_date: startDate,
          end_date: endDate,
        };
  if (startTime) {
    body['start_time'] = startTime;
  }
  if (endTime) {
    body['end_time'] = endTime;
  }
  if (trimmedNotes.length > 0) {
    body['admin_notes'] = trimmedNotes;
  }
  if (formId) {
    body['form_id'] = formId;
  }

  try {
    const result = await apiService.postAPI(
      serviceRequestEndpoints.ASSIGN_TECHNICIAN(trimmedId),
      body,
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      const payload = (result.data ?? {}) as {
        data?: AssignTechnicianResponseData;
      };
      return {
        success: true,
        ...(payload.data ? { data: payload.data } : {}),
      };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status, assigneeType),
      ...(isAssignmentConflict(result.data, result.status)
        ? { conflict: true }
        : {}),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return {
          success: false,
          error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }
      return {
        success: false,
        error: extractErrorMessage(
          error.response?.data,
          error.response?.status,
          assigneeType
        ),
        ...(isAssignmentConflict(error.response?.data, error.response?.status)
          ? { conflict: true }
          : {}),
      };
    }
    return {
      success: false,
      error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
